import React, { useId, useRef, useState } from 'react'
import styled from 'styled-components'

import Chart from 'features/ask-yogi/features/conversation/components/Chart'
import type { AskYogiChartExportHandle } from 'features/ask-yogi/features/conversation/utils/capture-chart-slides'
import type { ArtifactChartPage } from 'features/ask-yogi/types'
import { isYogiQLStoredPanel } from 'features/charts-v2/types/stored-panel'

type Props = {
  page: ArtifactChartPage
  conversationUuid: string | undefined
  messageUuid: string
  chartKey: string
}

/**
 * The charts page a charts-page turn left, drawn one panel at a time.
 *
 * Each panel is its own query, so the selected panel is the only one mounted —
 * switching panels runs that panel's query, and nothing else does. "View in
 * charts" opens every panel, in page order, with the selected one active.
 */
const PageChart = React.forwardRef<AskYogiChartExportHandle, Props>(
  ({ page, conversationUuid, messageUuid, chartKey }, ref) => {
    const [chosen, setChosen] = useState(page.focus)
    const tabIdPrefix = useId()
    const tabRefs = useRef<Array<HTMLButtonElement | null>>([])
    // A stored message is whatever was saved: a focus past its panels opens on
    // the first one, and the tabs say so.
    const selected = page.panels[chosen] ? chosen : 0
    const shown = page.panels[selected]
    if (!shown) return null
    const hasTabs = page.panels.length > 1
    const tabId = (index: number): string => `${tabIdPrefix}-tab-${index}`
    const panelId = `${tabIdPrefix}-panel`

    // The WAI-ARIA tabs pattern: one tab stop for the row, arrows move along it.
    const onTabKeyDown = (event: React.KeyboardEvent): void => {
      const count = page.panels.length
      const targets: Partial<Record<string, number>> = {
        ArrowRight: (selected + 1) % count,
        ArrowLeft: (selected - 1 + count) % count,
        Home: 0,
        End: count - 1,
      }
      const next = targets[event.key]
      if (next === undefined) return
      event.preventDefault()
      setChosen(next)
      tabRefs.current[next]?.focus()
    }

    return (
      <div>
        {hasTabs && (
          <PanelTabs role="tablist" aria-label="Panels">
            {page.panels.map((p, index) => (
              <PanelTab
                key={p.position}
                ref={(el) => {
                  tabRefs.current[index] = el
                }}
                id={tabId(index)}
                type="button"
                role="tab"
                aria-selected={index === selected}
                aria-controls={panelId}
                tabIndex={index === selected ? 0 : -1}
                $active={index === selected}
                onClick={() => setChosen(index)}
                onKeyDown={onTabKeyDown}
              >
                Panel {p.position}
              </PanelTab>
            ))}
          </PanelTabs>
        )}
        <div
          {...(hasTabs && {
            id: panelId,
            role: 'tabpanel',
            'aria-labelledby': tabId(selected),
          })}
        >
          <Chart
            // A new panel is a new chart, not an update of the last one.
            key={shown.position}
            ref={ref}
            // A YogiQL panel is `{request: W}`, and the chat draws W from
            // the artifact's own form `{request: W, chart_type}`; under
            // `panel` it read as legacy and ran on the chat chart route.
            request={{
              ...(isYogiQLStoredPanel(shown.panel)
                ? { request: shown.panel.request }
                : { panel: shown.panel }),
              ...(shown.chart_type && { chart_type: shown.chart_type }),
            }}
            title={shown.title}
            page={page}
            pageIndex={selected}
            conversationUuid={conversationUuid}
            messageUuid={messageUuid}
            chartKey={chartKey}
          />
        </div>
      </div>
    )
  },
)

PageChart.displayName = 'PageChart'

export default PageChart

const PanelTabs = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 8px;
`

const PanelTab = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 4px;
  border: 1px solid #d1d5db;
  background: ${({ $active }) => ($active ? '#3b82f6' : '#fff')};
  color: ${({ $active }) => ($active ? '#fff' : '#374151')};
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  &:hover {
    background: ${({ $active }) => ($active ? '#2563eb' : '#f3f4f6')};
  }
`
